import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { createBrowserRouter, Navigate, RouterProvider, useLocation, type RouteObject } from "react-router";
import "./styles.css";
import { ApiError } from "./api.ts";
import { BrandEffects, Shell } from "./components/Shell.tsx";
import { TabbedLayout } from "./components/ui.tsx";
import { ADMIN_GROUPS, LEGACY_ADMIN_PATHS } from "./lib/admin-nav.ts";
import { McpPage } from "./pages/Mcp.tsx";
import { AdminMcpPage } from "./pages/AdminMcp.tsx";
import { BrandingPage } from "./pages/Branding.tsx";
import { LoginPage } from "./pages/Login.tsx";
import { HomePage } from "./pages/Home.tsx";
import { UsersPage } from "./pages/Users.tsx";
import { GroupsPage } from "./pages/Groups.tsx";
import { SsoPage } from "./pages/Sso.tsx";
import { AuditPage } from "./pages/Audit.tsx";
import { RuntimesPage } from "./pages/Runtimes.tsx";
import { ModelsPage } from "./pages/Models.tsx";
import { UsagePage } from "./pages/Usage.tsx";
import { SecretsPage } from "./pages/Secrets.tsx";
import { SettingsPage } from "./pages/Settings.tsx";
import { ConnectionsPage } from "./pages/Connections.tsx";
import { ChatPage } from "./pages/Chat.tsx";
import { ApprovalsPage } from "./pages/Approvals.tsx";
import { FilesPage } from "./pages/Files.tsx";
import { TasksPage } from "./pages/Tasks.tsx";
import { MessagingPage } from "./pages/Messaging.tsx";
import { LinkPage } from "./pages/Link.tsx";
import { MemoryPage } from "./pages/Memory.tsx";
import { SkillsAdminPage } from "./pages/Skills.tsx";
import { MySkillsPage } from "./pages/MySkills.tsx";
import { SyncPage } from "./pages/Sync.tsx";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { retry: (n, e) => !(e instanceof ApiError && e.status < 500) && n < 2, refetchOnWindowFocus: false },
  },
});

/** The page behind each admin tab, by "group/tab". */
const adminPages: Record<string, React.ReactNode> = {
  "people/users": <UsersPage />,
  "people/groups": <GroupsPage />,
  "people/sso": <SsoPage />,
  "people/audit": <AuditPage />,
  "assistants/list": <RuntimesPage />,
  "assistants/models": <ModelsPage />,
  "assistants/usage": <UsagePage />,
  "knowledge/memory": <MemoryPage />,
  "knowledge/skills": <SkillsAdminPage />,
  "knowledge/sync": <SyncPage />,
  "integrations/mcp": <AdminMcpPage />,
  "integrations/messaging": <MessagingPage />,
  branding: <BrandingPage />,
};

/** Old flat URLs keep working (bookmarks, OAuth return links, messages), with ?query and #hash. */
function LegacyRedirect({ to }: { to: string }) {
  const { search, hash } = useLocation();
  return <Navigate to={`/admin/${to}${search}${hash}`} replace />;
}

const adminRoutes: RouteObject = {
  path: "admin",
  children: [
    { index: true, element: <Navigate to={ADMIN_GROUPS[0]!.base} replace /> },
    ...ADMIN_GROUPS.map((g): RouteObject => {
      if (!g.tabs.length) return { path: g.base, element: adminPages[g.base] };
      return {
        path: g.base,
        element: <TabbedLayout tabs={g.tabs.map((t) => ({ to: t.path, label: t.label }))} />,
        children: [
          { index: true, element: <Navigate to={g.tabs[0]!.path} replace /> },
          ...g.tabs.map((t): RouteObject => ({ path: t.path, element: adminPages[`${g.base}/${t.path}`] })),
        ],
      };
    }),
    ...Object.entries(LEGACY_ADMIN_PATHS).map(([from, to]): RouteObject => ({ path: from, element: <LegacyRedirect to={to} /> })),
  ],
};

const router = createBrowserRouter([
  { path: "/login", element: <LoginPage /> },
  {
    element: <Shell />,
    children: [
      // Chat is the front door; the old home page is the dashboard.
      { index: true, element: <Navigate to="/chat" replace /> },
      { path: "dashboard", element: <HomePage /> },
      // One route (optional id) so the page stays mounted when a new chat gets its id mid-stream.
      { path: "chat/:sessionId?", element: <ChatPage /> },
      { path: "approvals/:id?", element: <ApprovalsPage /> },
      { path: "files", element: <FilesPage /> },
      { path: "tasks", element: <TasksPage /> },
      { path: "secrets", element: <SecretsPage /> },
      { path: "settings", element: <SettingsPage /> },
      { path: "skills", element: <MySkillsPage /> },
      { path: "link/:code", element: <LinkPage /> },
      { path: "connections", element: <ConnectionsPage /> },
      { path: "mcp", element: <McpPage /> },
      adminRoutes,
    ],
  },
]);

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <BrandEffects />
      <RouterProvider router={router} />
    </QueryClientProvider>
  </StrictMode>,
);
